import { useEffect, type RefObject } from 'react';

/**
 * Message type posted by the /embed/form page to its parent window so the
 * host page can resize the iframe to the card height (ENR-1474). The copied
 * embed snippet listens for this exact type.
 */
export const EMBED_RESIZE_MESSAGE_TYPE = 'enrollio-form-resize';

/**
 * Full rendered height of the embedded form wrapper, rounded up to whole
 * pixels. scrollHeight covers content that overflows the wrapper, the rect
 * covers content that does not. Returns 0 without an element so callers
 * never post a collapsing frame size.
 */
export function embedContentHeight(el: HTMLElement | null): number {
  if (!el) return 0;
  return Math.ceil(
    Math.max(el.scrollHeight, el.getBoundingClientRect().height),
  );
}

/**
 * Posts the wrapper element's height to the parent window on mount and
 * whenever it resizes (success state, validation messages, viewport
 * width). No-op outside an iframe, so the snippet keeps native iframe
 * scrolling as the fallback when this script cannot run.
 */
export function useEmbedAutoResize(
  targetRef: RefObject<HTMLElement | null>,
): void {
  useEffect(() => {
    if (window.parent === window) return;
    const post = () => {
      const height = embedContentHeight(targetRef.current);
      if (height > 0) {
        window.parent.postMessage(
          { type: EMBED_RESIZE_MESSAGE_TYPE, height },
          '*',
        );
      }
    };
    post();
    const el = targetRef.current;
    if (!el) return;
    const observer = new ResizeObserver(() => { post(); });
    observer.observe(el);
    return () => { observer.disconnect(); };
  }, [targetRef]);
}
